import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('iwx_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 → try refresh
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem('iwx_refresh_token');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          localStorage.setItem('iwx_access_token', data.access_token);
          localStorage.setItem('iwx_refresh_token', data.refresh_token);
          original.headers.Authorization = `Bearer ${data.access_token}`;
          return api(original);
        } catch {
          localStorage.removeItem('iwx_access_token');
          localStorage.removeItem('iwx_refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth ──────────────────────────────────────────────
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  refresh: (data) => api.post('/auth/refresh', data),
  me: () => api.get('/auth/me'),
};

// ─── Public ────────────────────────────────────────────
export const publicAPI = {
  getData: () => api.get('/public/data'),
};

// ─── Users ─────────────────────────────────────────────
export const usersAPI = {
  list: () => api.get('/users/'),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// ─── Generic CRUD helper ───────────────────────────────
const crud = (resource) => ({
  list: () => api.get(`/${resource}`),
  create: (data) => api.post(`/${resource}`, data),
  update: (id, data) => api.put(`/${resource}/${id}`, data),
  patch: (id, field, value) =>
    api.patch(`/${resource}/${id}`, { field, value }),
  delete: (id) => api.delete(`/${resource}/${id}`),
});

export const companyAPI = {
  get: () => api.get('/company'),
  patchField: (key, field, value) =>
    api.patch(`/company/${key}`, { field, value }),
};
export const companyValuesAPI = crud('company-values');
export const socialLinksAPI = crud('social-links');
export const navigationAPI = crud('navigation');
export const statsAPI = crud('stats');
export const productsAPI = crud('products');
export const servicesAPI = crud('services');
export const testimonialsAPI = crud('testimonials');
export const howItWorksAPI = crud('how-it-works');
export const teamAPI = crud('team');
export const careersAPI = crud('careers');
export const faqsAPI = crud('faqs');
export const partnersAPI = crud('partners');

export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  list: () => api.get('/contact-messages'),
  markRead: (id) => api.patch(`/contact-messages/${id}/read`),
  delete: (id) => api.delete(`/contact-messages/${id}`),
};

export const newsletterAPI = {
  subscribe: (email) => api.post('/newsletter/subscribe', { email }),
  list: () => api.get('/newsletter/subscribers'),
};

export default api;
